/*
 * @license
 * Copyright© Celligence International, LLC - 2023
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */

'use client';

import { useEffect } from 'react';

/**
 * Unified Footer Platform loader.
 *
 * Replaces the hand-rolled <Footer/> that fetched NEXT_PUBLIC_CDN_FOOTER_JSON and
 * re-implemented the markup locally: the branded footer (logo, disclaimer, contact
 * block, important links, sitemap, social icons, awards, copyright) is now fetched
 * AND rendered at runtime by the CDN-hosted script at
 * `${NEXT_PUBLIC_FOOTER_CDN_BASE}loader.js`.
 *
 * Same convention as this app's AngelAiWidget/UserWayScript/HeaderWidget: a small
 * local file with no npm dependency, injects in an effect, renders null.
 *
 * `domainOverride="angelai"` is forced unconditionally. The loader resolves which
 * branded footer to render from a hostname-keyed registry; on any host that is not a
 * real angelai.com subdomain (localhost, a LAN IP, a preview URL) auto-detection
 * silently falls back to the registry default ("swmc"). Forcing the key is harmless
 * in production, where the hostname would resolve to it anyway.
 */
export default function FooterWidget({
  language = 'en',
  lightTheme = false,
  showEqualOpportunityLogo = false,
  showTopDisclaimer = true,
  cdnBase,
  mountId = 'global-footer',
  bgColor,
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Attributes are re-applied on EVERY effect run, before the script guard below.
    // React 18 Strict Mode (next dev) double-invokes mount effects and can recreate
    // the JSX-rendered mount div between runs, while the manually appended <script>
    // persists in document.head across that -- guarding first would leave the fresh
    // mount div with no attributes at all.
    let mount = document.getElementById(mountId);
    if (!mount) {
      mount = document.createElement('div');
      mount.id = mountId;
      document.body.appendChild(mount);
    }
    mount.setAttribute('data-language', language);
    mount.setAttribute('data-domain-override', 'angelai');
    mount.setAttribute('data-light-theme', lightTheme ? 'true' : 'false');
    // The CDN's Angelai-Celligence.json carries the same disclaimer.angelaiDisc /
    // servicesDisc paragraphs the removed local footer rendered above its main row,
    // so this stays on to preserve that copy.
    mount.setAttribute('data-show-top-disclaimer', showTopDisclaimer ? 'true' : 'false');
    mount.setAttribute(
      'data-show-equal-opportunity-logo',
      showEqualOpportunityLogo ? 'true' : 'false'
    );
    if (cdnBase) mount.setAttribute('data-cdn-base', cdnBase);
    // Read by the loader's __fbgApply() -- injects a <style> that sets
    // --footer-bg/--footer-top-section-bg with !important. Only present on the
    // staging v2 bundle as of 2026-09-22; a stale/prod bundle without it silently
    // ignores the attribute, so this is a no-op there until the bundle is promoted.
    if (bgColor) mount.setAttribute('data-bg-color', bgColor);
    else mount.removeAttribute('data-bg-color');

    // Only the injection itself is guarded -- running it twice would fetch and
    // execute the CDN loader a second time.
    const SCRIPT_ID = 'footer-widget-script';
    if (document.getElementById(SCRIPT_ID)) return;

    const base = cdnBase || process.env.NEXT_PUBLIC_FOOTER_CDN_BASE;
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${base}loader.js?v=${Date.now()}`;
    script.defer = true;
    script.onerror = () => {
      // eslint-disable-next-line no-console
      console.error(`[footer-widget] failed to load ${script.src}`);
    };
    document.head.appendChild(script);
  }, [language, lightTheme, showEqualOpportunityLogo, showTopDisclaimer, cdnBase, mountId, bgColor]);

  return null;
}
